import type { ReactNode } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { xml } from "@codemirror/lang-xml";
import { json } from "@codemirror/lang-json";

// `widget="code"` — syntax-highlighted source viewer/editor (CodeMirror 6).
//
// First consumer: ir.application.view `arch` (view DSL XML). Declared in the
// DSL as `<field name="arch" widget="code" option-language="xml"/>`; the
// `option-language` attribute reaches us via widgetOptions.language.
//
// Read mode renders a read-only editor (highlight + line numbers, no caret);
// edit mode is the same surface, editable. Values that aren't strings (a JSON
// column holding an object) are pretty-printed for display; edits always
// write the raw string back — parse/validation stays a backend concern (the
// ir.application.view hooks dry-run the arch on save).

const LANGUAGE_EXTENSIONS: Record<string, () => unknown> = {
    xml,
    json,
};

function resolveExtensions(language: string | undefined): unknown[] {
    const factory = LANGUAGE_EXTENSIONS[(language ?? "").toLowerCase()];
    return factory ? [factory()] : [];
}

function toText(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

export function CodeEditorField({
    value,
    onChange,
    readOnly = false,
    language,
    placeholder,
}: {
    value: unknown;
    onChange?: (next: string) => void;
    readOnly?: boolean;
    language?: string;
    placeholder?: string;
}): ReactNode {
    const text = toText(value);
    if (readOnly && text === "") {
        return <span className="cell-muted">—</span>;
    }
    return (
        <div className={readOnly ? "code-editor code-editor--read" : "code-editor"}>
            <CodeMirror
                value={text}
                readOnly={readOnly}
                editable={!readOnly}
                placeholder={placeholder}
                extensions={resolveExtensions(language) as never[]}
                basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: !readOnly,
                    highlightActiveLineGutter: !readOnly,
                }}
                minHeight={readOnly ? undefined : "120px"}
                maxHeight="420px"
                onChange={(next: string) => onChange?.(next)}
            />
        </div>
    );
}
